import {
  ChangeDetectorRef,
  Component,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';

import { AppState } from 'src/app/core/state/app.state';
import { FormProperty } from 'src/app/core/models/form.models';
import { FormsService } from 'src/app/core/services/forms.service';
import { LoggerService } from '@services/logger.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { WidgetSettingsComponent } from '../widget-settings/widget-settings.component';
import { WidgetSettingsService } from 'src/app/widgets/services/widget.settings.service';
import { DynamicFormComponent } from '../../../../shared/components/dynamic-form/dynamic-form.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-widget-settings-chart-axes',
  templateUrl: './widget-settings-chart-axes.component.html',
  styleUrls: ['./widget-settings-chart-axes.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    forwardRef(() => DynamicFormComponent),
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class WidgetSettingsChartAxesComponent extends WidgetSettingsComponent {
  constructor(
    protected formService: FormsService,
    protected widgetSettings: WidgetSettingsService,
    protected cdf: ChangeDetectorRef,
    private translateService: TranslateService,
    protected store: Store<AppState>,
    protected logger: LoggerService,
  ) {
    super(formService, widgetSettings, cdf, store, logger);
  }

  formProperties: FormProperty[] = [
    {
      propertyName: 'settings.gradient_color_min',
      propertyGroup: {
        key: 'settings.heatmap-colors',
      },
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.color-gradient-minimum',
      ),
      controlType: 'colorpicker',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.gradient_color_max',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.color-gradient-maximum',
      ),
      propertyGroup: {
        key: 'settings.heatmap-colors',
      },
      columnIndex: 1,
      controlType: 'colorpicker',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.show_x_axis_title',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.show-x-axis-title',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
        tripleStateMode: this.massUpdate ? 'nullable' : 'none',
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.title.font_size',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-font-size',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.title.align',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-title-align',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.itemHorizontalAlignOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.title.rotation',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-title-rotation',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'degrees °',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.title.color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-title-color',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'colorpicker',
      isMassUpdate: true,
      //columnIndex: 1,
    },
    {
      propertyName: 'settings.show_y_axis_title',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.show-y-axis-title',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
        tripleStateMode: this.massUpdate ? 'nullable' : 'none',
      },
      columnIndex: 1,
      isMassUpdate: true,
    },

    {
      propertyName: 'settings.y_axis.title.font_size',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-font-size',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.title.align',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-title-align',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.itemHorizontalAlignOptions,
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.title.rotation',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-title-rotation',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'degrees °',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.title.color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-title-color',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'colorpicker',
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.show_x_axis_labels',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.show-x-axis-labels',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
        tripleStateMode: this.massUpdate ? 'nullable' : 'none',
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.labels.font_size',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-labels-font-size',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.labels.wrap',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.wrap-x-axis-labels',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
        tripleStateMode: this.massUpdate ? 'nullable' : 'none',
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.labels.align',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-labels-align',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.itemHorizontalAlignOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.labels.rotation',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-labels-rotation',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'degrees °',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.labels.color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-labels-color',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'colorpicker',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.show_y_axis_labels',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.show-y-axis-labels',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
        tripleStateMode: this.massUpdate ? 'nullable' : 'none',
      },
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.labels.font_size',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-labels-font-size',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.labels.wrap',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.wrap-y-axis-labels',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
        tripleStateMode: this.massUpdate ? 'nullable' : 'none',
      },
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.labels.align',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-labels-align',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.itemHorizontalAlignOptions,
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.labels.rotation',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-labels-rotation',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'degrees °',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.labels.color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-labels-color',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'colorpicker',
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.show_x_axis_line',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.show-x-axis-line',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
        tripleStateMode: this.massUpdate ? 'nullable' : 'none',
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.line_width',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-line-width',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.line_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-line-color',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'colorpicker',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.show_y_axis_line',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.show-y-axis-line',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
        tripleStateMode: this.massUpdate ? 'nullable' : 'none',
      },
      columnIndex: 1,
      isMassUpdate: true,
    },

    {
      propertyName: 'settings.y_axis.line_width',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-line-width',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.line_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-line-color',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'colorpicker',
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.show_x_axis_ticks',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.show-x-axis-ticks',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
        tripleStateMode: this.massUpdate ? 'nullable' : 'none',
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.ticks.tick_width',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-tick-width',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.ticks.tick_length',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-tick-length',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.ticks.tick_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-tick-color',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'colorpicker',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.show_y_axis_ticks',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.show-y-axis-ticks',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
        tripleStateMode: this.massUpdate ? 'nullable' : 'none',
      },
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.ticks.tick_width',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-tick-width',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.ticks.tick_length',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-tick-length',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.ticks.tick_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-tick-color',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'colorpicker',
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.show_x_axis_grid_lines',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.show-x-axis-grid-lines',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
        tripleStateMode: this.massUpdate ? 'nullable' : 'none',
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.grid_line_width',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-grid-line-width',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.grid_line_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.x-axis-grid-line-color',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'colorpicker',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.show_y_axis_grid_lines',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.show-y-axis-grid-lines',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
        tripleStateMode: this.massUpdate ? 'nullable' : 'none',
      },
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.grid_line_width',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-grid-line-width',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.grid_line_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.y-axis-grid-line-color',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'colorpicker',
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.x_axis.max_padding',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.max-padding-x-axis',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.y_axis.max_padding',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-axes.label.max-padding-y-axis',
      ),
      propertyGroup: {
        key: 'settings.axis-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
  ];
}
